import {
  AfterViewInit,
  Component,
  EventEmitter,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import '../utilities/lib-tinymce/tinymce.min';
import '../utilities/lib-tinymce/plugins/link/plugin.min';
import '../utilities/lib-tinymce/plugins/paste/plugin.min';
import '../utilities/lib-tinymce/plugins/placeholder/plugin.min';
import '../utilities/lib-tinymce/plugins/table/plugin.min';
import '../utilities/lib-tinymce/plugins/wordcount/plugin.min';
import '../utilities/lib-tinymce/plugins/fullscreen/plugin.min';
import '../utilities/lib-tinymce/plugins/spellchecker/plugin.min';
import '../utilities/lib-tinymce/plugins/lists/plugin.min';
import '../utilities/lib-tinymce/themes/silver/theme.min';

declare const tinymce: any;

@Component({
  selector: 'axis-editor',
  templateUrl: './editor.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EditorComponent),
      multi: true
    }
  ],
  host: { class: 'axis-editor', '(click)': 'onEditorFocusOut($event)', }
})
export class EditorComponent implements AfterViewInit, OnDestroy {

  @Input() elementId: string;
  @Input() height: any = 200;
  @Input() isReadOnly = false;
  @Input() maxLength: number;
  @Input() menubar: any = false;
  @Input() menubarFlag = false;
  @Input() placeHold: any = 'Please enter text.';
  @Input() plugins = ['paste', 'lists', 'placeholder', 'table', 'wordcount', 'spellchecker'];
  @Input() rel: string;
  @Input() resize: any = false;
  @Input() required = false;
  @Input() skinURL = './assets/skins/ui/oxide';
  @Input() spellcheckerLanguages = 'English=en_US'; // default english language is selected
  @Input() spellcheckerCallback: Function; // This is custom spellchecker function callback.
  // tslint:disable-next-line:max-line-length
  @Input() toolbar: any = 'insertfile undo redo | styleselect | paste | bold italic underline | strikethrough subscript superscript | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table | link';
  @Input() toolbarFlag = false;

  @Output() onEditorKeyup = new EventEmitter<any>();
  private isDisabled = false;
  public editor: any;
  public isInialLoadWriteValue = true;

  private menubarRef: any;
  private toolbarRef: any;

  private propagateModelChange = (_: any) => { };
  private OnTouchedEvent = () => { };

  @ViewChild('editorBody') editorBody: any;

  constructor(private el: ElementRef) { }

  ngAfterViewInit() {
    let self: any = '';
    self = this;
    tinymce.init({
      selector: '#' + this.elementId,
      branding: false,
      browser_spellcheck: this.spellcheckerCallback ? false : true,
      content_css: this.skinURL + '/content.min.css',
      content_style: 'body{min-height:0px !important}',
      elementpath: false,
      height: this.height,
      menubar: this.menubar,
      paste_as_text: true,
      paste_data_images: true,
      placeholder: this.placeHold,
      plugins: this.spellcheckerCallback ? this.addSpellcheckerToPlugins() : this.plugins,
      readonly: this.isReadOnly,
      resize: this.resize,
      required: this.required,
      skin_url: this.skinURL,
      spellchecker_languages: this.spellcheckerLanguages,
      theme: 'silver',
      toolbar: this.spellcheckerCallback ? this.addSpellcheckerToToolbar() : this.toolbar,
      init_instance_callback: (editor: any) => {
        const content = editor.getContent();
        this.editor.setContent(content);
      },
      spellchecker_callback: function (method, text, success, failure) {
        // call the user provided callback function for spellcheck.
        const wordsList = text.match(this.getWordCharPattern());
        if (self.spellcheckerCallback) {
          self.spellcheckerCallback(method, wordsList, success, failure);
        }
      },
      setup: editor => {
        this.editor = editor;
        editor.on('keyup', () => {
          const content = editor.getContent();
          this.OnTouchedEvent();
          this.propagateModelChange(content);
          this.onEditorKeyup.emit(content);
        });
        editor.on('keydown', event => {
          const allowedKeys = [
            'ArrowUp',
            'Up',
            'ArrowDown',
            'Down',
            'ArrowLeft',
            'ArrowRight',
            'Enter',
            'Escape',
            'Esc',
            'Tab',
            'Backspace',
            'PageUp',
            'PageDown',
            'Delete',
            'Home',
            'End'
          ];
          if (allowedKeys.indexOf(event.key) !== -1) {
            return true;
          }
          if (this.tinymce_getContentLength() + 1 > this.maxLength) {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
          return true;
        });
        editor.on('paste', () => {
          setTimeout(() => {
            if (this.maxLength) {
              let pastedText = tinymce.get(tinymce.activeEditor.id).contentDocument.body.innerText;
              let trimmedText = pastedText.substring(0, this.maxLength);
              tinymce.get(tinymce.activeEditor.id).contentDocument.body.innerText = trimmedText;
            }
          }, 0);
        })
        editor.on('blur', () => {
          const content = this.editor.getContent();
          this.OnTouchedEvent();
          this.propagateModelChange(content);
          this.onEditorKeyup.emit(content);
          if (this.menubarRef && this.menubarFlag) {
            this.menubarRef.hidden = this.menubarFlag;
            this.disableMenubar(this.isDisabled);
          }
          if (this.toolbarRef) {
            this.toolbarRef.hidden = this.toolbarFlag;
            this.disableToolbar(this.isDisabled);
          }
        });
        editor.on('focus click', () => {
          if (this.isDisabled) {
            return;
          }
          if (this.menubarRef && this.menubarFlag) {
            this.menubarRef.hidden = this.menubarFlag ? false : this.menubarFlag;
          }
          if (this.toolbarRef) {
            this.toolbarRef.hidden = this.toolbarFlag ? false : this.toolbarFlag;
          }
        });
      }
    });

    setTimeout(() => {
      this.menubarRef = this.el.nativeElement.querySelector('.tox-menubar');
      this.toolbarRef = this.el.nativeElement.querySelector('.tox-toolbar');
      if (this.menubarFlag && this.menubarRef) {
        this.menubarRef.hidden = this.menubarFlag;
      }
      if (this.toolbarRef) {
        this.toolbarRef.hidden = this.toolbarFlag;
      }
    }, 200);
  }


  /**
   * Add spell checker when no spell checker is present in toolbar
   */
  addSpellcheckerToToolbar() {
    if (this.toolbar.search('spellchecker') >= 0) {
      return this.toolbar;
    } else {
      this.toolbar = this.toolbar + ' | spellchecker ';
      return this.toolbar;
    }
  }

  /**
   * Add spell checker when no spell checker is present in plugins.
   */
  addSpellcheckerToPlugins() {
    let spellcheckerPlugin = false;
    if (this.plugins && Array.isArray(this.plugins)) {
      const length = this.plugins.length;
      for (let i = 0; i < length; i++) {
        if (this.plugins[i] === 'spellchecker') {
          spellcheckerPlugin = true;
          break;
        }
      }
      if (!spellcheckerPlugin) {
        this.plugins.push('spellchecker');
      }
    }
    return this.plugins;
  }

  onEditorFocusOut(event) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.OnTouchedEvent();
      setTimeout(() => {
        if (this.menubarFlag && this.menubarRef) {
          this.menubarRef.hidden = this.menubarFlag;
        }
        if (this.toolbarRef) {
          this.toolbarRef.hidden = this.toolbarFlag;
        }
      }, 200);
    }
  }

  writeValue(value) {
    if (value) {
      setTimeout(() => { // settimeout to fix formcontrol issue
        this.editor.setContent(value);
      });
    } else {
      if (!this.isInialLoadWriteValue && value === null) {
        this.editor.setContent('');
      }
    }
  }

  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
    this.isInialLoadWriteValue = false;
  }

  registerOnTouched(fn: any): void {
    this.OnTouchedEvent = fn;
  }

  // Allows Angular to disable the input.
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (!this.editor) {
        setTimeout(() => {
          this.disableEditor(isDisabled);
        }, 0);
        return;
      }
     this.disableEditor(isDisabled);
  }

  private disableToolbar(isDisabled: boolean) {
    if (this.toolbarRef) {
      this.toolbarRef.querySelectorAll('button').forEach((node: any) => {
        node['disabled'] = isDisabled;
        if (isDisabled) {
          node.style['pointer-events'] = 'none';
        } else {
          node.style['pointer-events'] = 'auto';
        }
      });
    }
  }

  private disableMenubar(isDisabled: boolean) {
    if (this.menubarRef) {
      this.menubarRef.querySelectorAll('button').forEach((node: any) => {
        node['disabled'] = isDisabled;
      });
    }
  }

  private disablePlaceHolder(isDisabled: boolean) {
    if (this.plugins.toString().indexOf('placeholder') > -1) {
      const placeHolder = this.editorBody.nativeElement.parentNode.querySelector('.tox-edit-area');
      if (placeHolder) {
        if (isDisabled) {
          placeHolder.style['pointer-events'] = 'none';
        } else {
          placeHolder.style['pointer-events'] = 'unset';
        }
      }
    }
  }

  private disableEditor(isDisabled: boolean) {
    if (this.editor.getBody()) {
      this.editor.getBody().setAttribute('contenteditable', !isDisabled);
    }
    if (this.toolbarRef) {
      this.disableToolbar(isDisabled);
    } else if (!this.toolbarFlag) {
      setTimeout(() => {
        this.disableToolbar(isDisabled);
      }, 1000);
    }
    if (this.menubarRef) {
      this.disableMenubar(isDisabled);
    } else if (!this.menubarFlag) {
      setTimeout(() => {
        this.disableMenubar(isDisabled);
      }, 1000);
    }
    this.disablePlaceHolder(isDisabled);
  }

  validate(control: AbstractControl): { [key: string]: any } {
    if (this.required) {
      if (control.value || !control.dirty) {
        return null;
      }
      return { axiseditor: { valid: false } };
    }
  }

  tinymce_getContentLength() {
    return tinymce.get(tinymce.activeEditor.id).contentDocument.body.innerText
      .length;
  }

  ngOnDestroy() {
    tinymce.remove(this.editor);
  }
}
